import { useEffect, useRef, useState } from 'react'

/**
 * Das Zahlenfeld.
 *
 * Trainiert freies Abrufen statt Wiedererkennen und ist damit naeher an dem,
 * was in der Mathearbeit verlangt wird. Deshalb bleibt es im Programm, auch
 * wenn die Ballons mehr Spass machen.
 *
 * Entscheidend ist die Zeitmessung: Gestoppt wird bis zum ERSTEN Tastendruck,
 * nicht bis zum Absenden. 4 x 2 = 8 ist eine Taste, 6 x 7 = 42 sind zwei.
 * Bis zum Absenden gemessen waere jede zweistellige Antwort systematisch
 * langsamer, und die ganze Statistik damit schief.
 */
export function Zahlenfeld({
  aufgabenId,
  stoppuhr,
  gesperrt,
  onAntwort,
}: {
  aufgabenId: string
  /** Wird hochgezaehlt, wenn die Messung neu beginnen soll (Rueckkehr aus dem Hintergrund). */
  stoppuhr: number
  gesperrt: boolean
  onAntwort: (eingabe: number, zeitMs: number) => void
}) {
  const [eingabe, setEingabe] = useState('')
  const start = useRef(Date.now())
  const ersterDruck = useRef<number | null>(null)
  /**
   * Die Eingabe zusaetzlich in einem Ref.
   *
   * Nicht doppelt gemoppelt, sondern noetig: Der Zustand aus useState ist
   * innerhalb desselben Ereignisdurchlaufs noch der alte. Tippt Lina "42" und
   * trifft den Haken im selben Wimpernschlag, laese absenden() sonst einen
   * leeren Wert und die Antwort ginge verloren. Das Ref ist sofort aktuell.
   */
  const eingabeRef = useRef('')
  /** Sperre gegen doppeltes Abschicken im selben Ereignisdurchlauf. */
  const abgeschickt = useRef(false)

  function setzen(wert: string) {
    eingabeRef.current = wert
    setEingabe(wert)
  }

  useEffect(() => {
    start.current = Date.now()
    ersterDruck.current = null
    abgeschickt.current = false
    setzen('')
  }, [aufgabenId, stoppuhr])

  function ziffer(z: number) {
    if (gesperrt) return
    // Der Moment der ersten Ziffer ist der Moment, in dem die Antwort im
    // Kopf war. Alles danach ist Tipparbeit und gehoert nicht zur Abrufzeit.
    if (ersterDruck.current === null) ersterDruck.current = Date.now() - start.current
    if (eingabeRef.current.length >= 3) return
    setzen(eingabeRef.current + String(z))
  }

  function loeschen() {
    if (gesperrt) return
    setzen(eingabeRef.current.slice(0, -1))
  }

  function absenden() {
    if (gesperrt || abgeschickt.current || eingabeRef.current === '') return
    abgeschickt.current = true
    onAntwort(Number(eingabeRef.current), ersterDruck.current ?? Date.now() - start.current)
  }

  // Eine Tastatur ist auf dem Tablet nicht im Spiel, am Rechner beim
  // Entwickeln aber sehr wohl.
  useEffect(() => {
    function taste(e: KeyboardEvent) {
      if (gesperrt) return
      if (e.key >= '0' && e.key <= '9') ziffer(Number(e.key))
      else if (e.key === 'Backspace') loeschen()
      else if (e.key === 'Enter') absenden()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  })

  return (
    <div className="zahlenfeld">
      <div className={eingabe ? 'anzeige' : 'anzeige anzeige--leer'}>{eingabe || '?'}</div>

      <div className="tasten">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((z) => (
          <button key={z} className="taste" onClick={() => ziffer(z)} type="button">
            {z}
          </button>
        ))}
        <button className="taste taste--weg" onClick={loeschen} type="button" aria-label="Löschen">
          ←
        </button>
        <button className="taste" onClick={() => ziffer(0)} type="button">
          0
        </button>
        <button
          className="taste taste--los"
          onClick={absenden}
          type="button"
          aria-label="Antwort abschicken"
        >
          ✓
        </button>
      </div>
    </div>
  )
}
